import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, LogOut, LayoutDashboard, ShieldCheck, Menu, X, MessageCircle } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const navLink = (to, label) => (
    <NavLink
      to={to}
      onClick={() => setOpen(false)}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `text-sm font-medium transition-colors px-2 py-1 ${
          isActive ? "text-[#0B3C5D]" : "text-slate-600 hover:text-[#0B3C5D]"
        }`
      }
    >
      {label}
    </NavLink>
  );

  return (
    <header className="sticky top-0 z-50 glass border-b border-slate-200/60" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-xl bg-navy-gradient flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-xl font-bold tracking-tight text-[#0B3C5D]">
              Gifto<span className="text-[#00C853]">pay</span>
            </span>
          </Link>
          <a
            href="https://wa.me/+10000000000?text=Hi%20Giftopay%2C%20I%20need%20help"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp support"
            data-testid="nav-whatsapp"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366]/10 text-[#128C36] hover:bg-[#25D366] hover:text-white border border-[#25D366]/30 px-3 py-1.5 text-xs font-semibold transition"
          >
            <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.5} />
            <span className="hidden sm:inline">WhatsApp</span>
          </a>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {navLink("/", "Home")}
          {navLink("/sell", "Sell")}
          {navLink("/rates", "Rates")}
          {navLink("/proof", "Proof of Payments")}
          {navLink("/faq", "FAQ")}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user && user.role ? (
            <>
              {user.role === "admin" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="text-[#0B3C5D]"
                  data-testid="nav-admin-btn"
                >
                  <ShieldCheck className="w-4 h-4 mr-1.5" /> Admin
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-[#0B3C5D]"
                data-testid="nav-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> Dashboard
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                data-testid="nav-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Logout
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/login")}
                className="rounded-full border-[#0B3C5D] text-[#0B3C5D] hover:bg-[#0B3C5D] hover:text-white"
                data-testid="nav-login-btn"
              >
                Log in
              </Button>
              <Button
                size="sm"
                className="rounded-full px-5 btn-primary"
                onClick={() => navigate("/sell")}
                data-testid="nav-sellnow-btn"
              >
                Sell Now
              </Button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-lg text-[#0B3C5D]"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLink("/", "Home")}
            {navLink("/sell", "Sell")}
            {navLink("/rates", "Rates")}
            {navLink("/proof", "Proof of Payments")}
            {navLink("/faq", "FAQ")}
            <div className="border-t border-slate-200 my-2" />
            {user && user.role ? (
              <>
                <Button variant="outline" onClick={() => { navigate("/dashboard"); setOpen(false); }} data-testid="nav-mobile-dashboard">
                  Dashboard
                </Button>
                {user.role === "admin" && (
                  <Button variant="outline" onClick={() => { navigate("/admin"); setOpen(false); }} data-testid="nav-mobile-admin">
                    Admin
                  </Button>
                )}
                <Button variant="outline" onClick={async () => { await logout(); setOpen(false); navigate("/"); }} data-testid="nav-mobile-logout">
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { navigate("/login"); setOpen(false); }} data-testid="nav-mobile-login">
                  Log in
                </Button>
                <Button className="btn-primary rounded-full" onClick={() => { navigate("/sell"); setOpen(false); }} data-testid="nav-mobile-sell">
                  Sell Now
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
